import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Trash2, Plus } from 'lucide-react';
import { formatTime, DAYS_OF_WEEK } from '../../lib/utils';

const SchedulesTab = ({
  schedules,
  classes,
  terms,
  rooms,
  scheduleForm,
  setScheduleForm,
  scheduleModalOpen,
  setScheduleModalOpen,
  handleScheduleSubmit,
  handleDeleteSchedule
}) => {
  return (
    <div data-testid="schedules-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Schedules</h2>
        <Button 
          onClick={() => setScheduleModalOpen(true)}
          className="bg-[#7000FF] hover:bg-[#5800CC]"
          data-testid="add-schedule-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Schedule
        </Button>
      </div>

      <div className="grid gap-4">
        {DAYS_OF_WEEK.map((day, dayIndex) => {
          const daySchedules = schedules.filter(s => s.day_of_week === dayIndex);
          if (daySchedules.length === 0) return null;
          return (
            <Card key={day} className="bg-[#111] border-[#333]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">{day}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {daySchedules.map(schedule => (
                    <div key={schedule.id} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                      <div>
                        <p className="font-medium text-white">{schedule.class_name}</p>
                        <p className="text-sm text-white/50">
                          {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)} • {schedule.room_name}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="text-red-400/50 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {schedules.length === 0 && (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="py-12 text-center text-white/50">
              No schedules yet. Create your first schedule!
            </CardContent>
          </Card>
        )}
      </div>

      {/* Schedule Modal */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent className="bg-[#111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-white">Add Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Class *</Label>
              <Select 
                value={scheduleForm.class_id} 
                onValueChange={v => setScheduleForm({...scheduleForm, class_id: v})}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Term *</Label>
              <Select 
                value={scheduleForm.term_id} 
                onValueChange={v => setScheduleForm({...scheduleForm, term_id: v})}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                  <SelectValue placeholder="Select term" />
                </SelectTrigger>
                <SelectContent>
                  {terms.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Room *</Label>
              <Select 
                value={scheduleForm.room_id} 
                onValueChange={v => setScheduleForm({...scheduleForm, room_id: v})}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                  <SelectValue placeholder="Select room" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Day *</Label>
              <Select 
                value={scheduleForm.day_of_week?.toString()} 
                onValueChange={v => setScheduleForm({...scheduleForm, day_of_week: parseInt(v)})}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((day, i) => (
                    <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Start Time *</Label>
                <Input 
                  type="time" 
                  value={scheduleForm.start_time} 
                  onChange={e => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">End Time *</Label>
                <Input 
                  type="time" 
                  value={scheduleForm.end_time} 
                  onChange={e => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleModalOpen(false)} className="border-[#333] text-white/70">
              Cancel
            </Button>
            <Button onClick={handleScheduleSubmit} className="bg-[#7000FF] hover:bg-[#5800CC]" data-testid="save-schedule-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulesTab;
